import { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import toNumber from 'lodash/toNumber';
import {
  API,
  NavigationHelper,
  LocationHelper,
  ToastActions,
  SidePanelContainer,
  SidePanelHeader,
  SidePanelContent,
  SidePanelFooter,
  SidePanelSection,
  VibeModal,
  VibeTooltip,
  VibeButton,
  VibeIcon,
  viClose,
  viCopy,
  viArchive,
  viUnarchive,
  withRouter,
  color,
} from 'vibeguide';
import Information from './Information';
import Address from './Address';
import Settings from './Settings';
import DisplayInformation from './DisplayInformation';
import './LocationDetails.scss';

class LocationDetails extends PureComponent {
  constructor(props) {
    super(props);

    this.imageFile = null;

    this.state = {
      confirmArchive: false,
      confirmationCode: null,
      confirmationMessage: null,
    };
  }

  /**
   * When the archive button is clicked
   */
  onClickArchive = async () => {
    const {
      locationData,
    } = this.props;

    const { code, message } = await LocationHelper.archiveLocation(locationData);

    this.setState({
      confirmArchive: true,
      confirmationCode: code,
      confirmationMessage: message,
    });
  };

  /**
   * When the unarchive button is clicked
   */
  onClickUnarchive = async () => {
    const {
      locationData: {
        _id,
      },
      onClose,
    } = this.props;

    await API.Location.reactivate({
      _id,
    });
    onClose();

    // tell listening components the object was saved
    document.dispatchEvent(new Event('onSaveLocation'));
  };

  /**
   * When the archive modal is closed
   */
  onCloseArchive = () => {
    this.setState({
      confirmArchive: false,
      confirmationCode: null,
      confirmationMessage: null,
    });
  };

  /**
   * When the archive modal is confirmed
   */
  onConfirmArchive = async () => {
    const {
      locationData: {
        _id,
      },
      onClose,
    } = this.props;

    const {
      confirmationCode,
    } = this.state;

    const responseDeleteComplete = await API.Location.deactivate(_id,
      { headers: { 'VAPI-ConfirmationCode': confirmationCode } });

    const successText = 'LOCATION.DEACTIVATED';
    const success = get(responseDeleteComplete, '[0].type') === successText;

    if (success) {
      this.onCloseArchive();
      onClose();

      // tell listening components the object was saved
      document.dispatchEvent(new Event('onSaveLocation'));
    }
  };

  onUploadImage = (file) => {
    this.imageFile = file;
  };

  /**
   * When duplicating, reset the image
   */
  onClickDuplicate = () => {
    const {
      onUpdate,
    } = this.props;

    this.imageFile = null;

    onUpdate({
      imageUrl: null,
    });
  };

  /**
   * Save the Location
   */
  onSave = async () => {
    const {
      locationData: {
        _id,
        name,
        companyId,
        category,
        tags = [],
        address1,
        address2,
        country,
        city,
        state,
        postalCode,
        geoCoordinatesLocked,
        lat,
        long,
        thirdPartyData,
        bannerId,
        bannerName,
        contentRating,
        adProgramEnabled,
        adNetworkEnabled,
        openExchangeEnabled,
        salesforceId,
        testLocation,
        mediaFormat,
        zoneId,
        impressionMultiplier,
        contentDeliverySystem,
        contentNetworkOwner,
        adNetworkOwner,
        adDeliverySystem,
        displaySettings,
      },
      history,
      isNew,
      queueToast,
    } = this.props;

    document.dispatchEvent(new Event('onSaveLocationStart'));

    queueToast({
      type: 'info',
      title: 'Saving...',
      allowClose: true,
    });

    // Get tags to add/remove
    const modifyTags = tags.filter(tag => tag.status === 'add' || tag.status === 'remove');
    const locationId = _id;

    // Form inputs allow empty strings for UX, so coerce numeric display
    // settings to finite numbers right before sending to the API.
    const toFiniteNumber = (value) => {
      const num = toNumber(value);
      return Number.isFinite(num) ? num : 0;
    };
    const toNumericPair = (pair) => [
      toFiniteNumber(pair?.[0]),
      toFiniteNumber(pair?.[1]),
    ];
    // Screen dimensions accept empty strings as "unset" — the API rejects 0
    // but allows '' — so preserve blanks instead of coercing to 0.
    const toScreenDimensionValue = (value) => {
      if (value === '' || value === undefined || value === null) {
        return '';
      }
      const num = toNumber(value);
      return Number.isFinite(num) ? num : '';
    };
    const toScreenDimensionsPair = (pair) => [
      toScreenDimensionValue(pair?.[0]),
      toScreenDimensionValue(pair?.[1]),
    ];
    // screenDiagonal follows the same "blank means unset" rules as
    // screenDimensions — the API rejects 0 but accepts a missing field.
    const toScreenDiagonalValue = (value) => {
      if (value === '' || value === undefined || value === null) {
        return '';
      }
      const num = toNumber(value);
      return Number.isFinite(num) ? num : '';
    };

    // Even when advanced is off the API still expects displayAreaDimensions /
    // displayAreaPosition to match the full screen resolution at the origin.
    const resolutionSize = toNumericPair(displaySettings?.resolution?.size);
    const screenDimensionsPair = toScreenDimensionsPair(displaySettings?.screenDimensions);
    const screenDiagonalValue = toScreenDiagonalValue(displaySettings?.screenDiagonal);
    const resolvedDisplaySettings = mediaFormat === 'visual'
      ? {
        ...displaySettings,
        resolution: displaySettings?.resolution
          ? {
            ...displaySettings.resolution,
            size: resolutionSize,
          }
          : displaySettings?.resolution,
        screenDimensions: screenDimensionsPair,
        screenDiagonal: screenDiagonalValue,
        displayAreaDimensions: toNumericPair(
          displaySettings && displaySettings.advancedEnabled
            ? displaySettings.displayAreaDimensions
            : resolutionSize,
        ),
        displayAreaPosition: toNumericPair(
          displaySettings && displaySettings.advancedEnabled
            ? displaySettings.displayAreaPosition
            : [0, 0],
        ),
      }
      : null;

    // The API rejects 0 for screenDimensions / screenDiagonal and treats a
    // missing field as "unset", so drop the fields entirely when blank.
    if (
      resolvedDisplaySettings
      && screenDimensionsPair[0] === ''
      && screenDimensionsPair[1] === ''
    ) {
      delete resolvedDisplaySettings.screenDimensions;
    }
    if (resolvedDisplaySettings && screenDiagonalValue === '') {
      delete resolvedDisplaySettings.screenDiagonal;
    }

    const visualData = mediaFormat === 'visual'
      ? {
        displaySettings: resolvedDisplaySettings,
      } : {};

    const includeData = {};

    if (impressionMultiplier > 0) {
      // include impression multiplier data
      includeData.impressionMultiplier = impressionMultiplier;
    }

    const data = {
      name,
      companyId,
      category,
      tags: modifyTags.map((tag) => {
        return {
          _id: tag._id,
          action: tag.status,
        };
      }),
      address1,
      address2,
      country,
      city,
      state,
      postalCode,
      geoCoordinatesLocked,
      lat,
      long,
      thirdPartyData,
      bannerId,
      bannerName,
      contentRating,
      adProgramEnabled,
      adNetworkEnabled,
      openExchangeEnabled,
      salesforceId,
      testLocation,
      mediaFormat,
      zoneId,
      contentDeliverySystem,
      contentNetworkOwner,
      adDeliverySystem: adNetworkEnabled ? adDeliverySystem : '',
      adNetworkOwner: adNetworkEnabled ? adNetworkOwner : '',
      ...visualData,
      ...includeData,
    };

    let modify = false;

    if (locationId && !isNew) {
      // Editing or saving an item
      data._id = locationId;
      modify = true;
    }

    try {
      const response = modify
        ? await API.Location.update(data)
        : await API.Location.create(data);
      const newLocationId = get(response, '[0].documentId', null);

      if (this.imageFile) {
        // Upload image
        const uploadImage = await API.Location.uploadImage(newLocationId, this.imageFile);
        const uploadImageId = get(uploadImage, '[0].documentId', null);

        if (uploadImageId) {
          // Image was successful
          queueToast({
            type: 'success',
            title: 'Saved!',
            allowClose: true,
          });
        } else {
          queueToast({
            type: 'error',
            title: 'Error Saving Location',
            timeout: 10,
            allowClose: true,
          });
        }
      } else {
        // No image to upload
        queueToast({
          type: 'success',
          title: 'Saved!',
          allowClose: true,
        });
      }

      if (isNew) {
        // Redirect user to the edit page for the new item
        history(NavigationHelper.updateParams({
          locationId: newLocationId,
          type: null,
        }));
      }

      // tell listening components the object was saved
      document.dispatchEvent(new Event('onSaveLocation'));
    } catch (err) {
      document.dispatchEvent(new Event('onSaveLocationError'));
    }
  };

  render() {
    const {
      user,
      locationData,
      isNew,
      onUpdate,
      onClose,
    } = this.props;

    const {
      confirmArchive,
      confirmationMessage,
    } = this.state;

    const disableInput = (locationData._id && !user.can('location.modify'))
      || (!locationData._id && !user.can('location.create'));

    const disableBannerField = !locationData.companyId;

    const visualDisplaySettings = locationData.displaySettings || {};
    const hasVisualIssue = locationData.mediaFormat === 'visual' ? (
      !visualDisplaySettings.resolution
      || !visualDisplaySettings.resolution.name
      || visualDisplaySettings.screenRotation === undefined
      || visualDisplaySettings.screenRotation === null
      || visualDisplaySettings.contentRotation === undefined
      || visualDisplaySettings.contentRotation === null
    ) : false;

    const disableSave = disableInput
      || !locationData.name
      || !locationData.companyId
      || !locationData.address1
      || !locationData.country
      || !locationData.city
      || ((locationData.country === 'US' || locationData.country === 'CA') && !locationData.state)
      || !locationData.postalCode
      || (locationData.mediaFormat !== 'visual' && !locationData.contentRating)
      || !locationData.bannerName
      || !locationData.mediaFormat
      || !locationData.category
      || hasVisualIssue
      || (locationData.adNetworkEnabled && !locationData.adDeliverySystem)
      || (locationData.adNetworkEnabled && !locationData.adNetworkOwner)
      || !locationData.contentDeliverySystem
      || !locationData.contentNetworkOwner
      || (locationData.adProgramEnabled && !locationData.adNetworkEnabled)
      || (locationData.openExchangeEnabled
        && (!locationData.adNetworkEnabled || !locationData.adProgramEnabled));

    const showArchive = user.can('location.delete');
    const isArchived = !locationData.active;

    return (
      <SidePanelContainer className="LocationDetails">
        <SidePanelHeader
          icons={(
            <VibeIcon
              className="close"
              icon={viClose}
              color={color.manatee}
              hoverColor={color.obsidian}
              size={24}
              onClick={onClose}
              data-testid="location-close-button"
            />
          )}
        >
          {locationData._id ? (
            <div className="title">
              <VibeTooltip title={locationData.name}>
                <span>
                  {locationData.name}
                </span>
              </VibeTooltip>
            </div>
          ) : (
            <div className="title">
              New Location
            </div>
          )}
        </SidePanelHeader>

        <SidePanelContent>
          <SidePanelSection title="Information">
            <Information
              name={locationData.name}
              companyId={locationData.companyId}
              companyName={locationData.companyName}
              bannerId={locationData.bannerId}
              bannerName={locationData.bannerName}
              category={locationData.category}
              salesforceId={locationData.salesforceId}
              testLocation={locationData.testLocation}
              mediaFormat={locationData.mediaFormat}
              zoneId={locationData.zoneId}
              zoneName={locationData.zoneName}
              impressionMultiplier={Math.floor((toNumber(get(locationData, 'impressionMultiplier', 0)) * 100))}
              contentRating={locationData.contentRating}
              image={locationData.imageUrl}
              tags={locationData.tags}
              disableInput={disableInput}
              isNew={isNew}
              onUpdate={onUpdate}
              onUploadImage={this.onUploadImage}
            />
          </SidePanelSection>

          <SidePanelSection title="Address">
            <Address
              address1={locationData.address1}
              address2={locationData.address2}
              country={locationData.country}
              city={locationData.city}
              locationState={locationData.state}
              postalCode={locationData.postalCode}
              geoCoordinatesLocked={locationData.geoCoordinatesLocked}
              lat={locationData.lat}
              long={locationData.long}
              disableInput={disableInput}
              isNew={isNew}
              onUpdate={onUpdate}
            />
          </SidePanelSection>

          {locationData.mediaFormat === 'visual' && (
            <SidePanelSection title={null}>
            <DisplayInformation
              displaySettings={locationData.displaySettings}
              canEdit={user.can('location.modify')}
              disableInput={disableInput}
              onUpdate={onUpdate}
            />
            </SidePanelSection>
          )}

          {user.sysAdmin && (
            <SidePanelSection title="Ad Information">
              <Settings
                contentRating={locationData.contentRating}
                companyId={locationData.companyId}
                mediaFormat={locationData.mediaFormat}
                canEdit={user.can('location.modify')}
                contentNetworkOwner={locationData.contentNetworkOwner}
                contentDeliverySystem={locationData.contentDeliverySystem}
                adNetworkOwner={locationData.adNetworkOwner}
                adDeliverySystem={locationData.adDeliverySystem}
                adProgramEnabled={locationData.adProgramEnabled}
                adNetworkEnabled={locationData.adNetworkEnabled}
                openExchangeEnabled={locationData.openExchangeEnabled}
                companyAdProgramEnabled={locationData.companyAdProgramEnabled}
                companyAdNetworkEnabled={locationData.companyAdNetworkEnabled}
                disableInput={disableInput}
                disableBannerField={disableBannerField}
                isNew={isNew}
                onUpdate={onUpdate}
              />
            </SidePanelSection>
          )}

        </SidePanelContent>

        <SidePanelFooter>
          <VibeButton
            text="Save Changes"
            color="secondary"
            loadingEvent="onSaveLocation"
            disabled={disableSave}
            testId="location-save-button"
            onClick={this.onSave}
          />

          {!isNew && (
            <>
              <VibeIcon
                icon={viCopy}
                type="button"
                buttonProps={{
                  size: 32,
                  borderColor: color.primary,
                }}
                color={color.primary}
                size={20}
                tooltip="Duplicate"
                link={NavigationHelper.updateParams({
                  type: 'new',
                })}
                onClick={this.onClickDuplicate}
                data-testid="location-duplicate-button"
              />

              {showArchive && !isArchived && (
                <VibeIcon
                  icon={viArchive}
                  type="button"
                  buttonProps={{
                    size: 32,
                    borderColor: color.error,
                  }}
                  tooltip="Archive"
                  color={color.error}
                  size={20}
                  onClick={this.onClickArchive}
                  data-testid="location-archive-button"
                />
              )}

              {showArchive && isArchived && (
                <VibeIcon
                  icon={viUnarchive}
                  type="button"
                  buttonProps={{
                    size: 32,
                    borderColor: color.success,
                  }}
                  tooltip="Unarchive"
                  color={color.success}
                  size={20}
                  onClick={this.onClickUnarchive}
                  data-testid="location-unarchive-button"
                />
              )}
            </>
          )}
        </SidePanelFooter>

        <VibeModal
          show={confirmArchive}
          type="confirm"
          title="Archive Location"
          confirmProps={{
            color: 'error',
          }}
          testIds={{
            root: 'archive-location-dialog',
            confirm: 'archive-location-confirm-button',
            cancel: 'archive-location-cancel-button',
          }}
          onConfirm={this.onConfirmArchive}
          onClose={this.onCloseArchive}
        >
          {confirmationMessage}
        </VibeModal>
      </SidePanelContainer>
    );
  }
}

LocationDetails.propTypes = {
  isNew: PropTypes.bool,
  locationData: PropTypes.oneOfType([
    PropTypes.object,
  ]),
  onClose: PropTypes.func,
  onUpdate: PropTypes.func,
};

LocationDetails.defaultProps = {
  isNew: false,
  locationData: {},
  onClose: () => {},
  onUpdate: () => {},
};

function mapStateToProps(state) {
  return {
    user: state.login.user,
  };
}

const mapDispatchToProps = {
  queueToast: ToastActions.queueToast,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LocationDetails));
